import { useSelector } from "react-redux";

import { createQueryKeys } from "@lukemorales/query-key-factory";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { camelCaseKeys } from "src/utility/utilityFunctions";

import { Budget } from "@models/Budget";
import client, { query } from "@services/api";
import { createConcurrencyLimiter } from "@utils/createConcurrencyLimiter";
import { buildPaginatedQuery, buildResourceMutations } from "@utils/reactQuery";

type OverBudgetBrand = { id: number; over_budget_count: number };

/**
 * Serial budget side-loads for OWR / Approvals / Rollup tables.
 * React Query still dedupes by queryKey; AbortSignal drops queued fetches
 * when a row/page unmounts (RQ best practice).
 */
const limitOrderSetBudgetFetch = createConcurrencyLimiter(1);

export const budgetsKeyFactory = createQueryKeys("budgets", {
  detail: (budgetId: string) => ({
    queryKey: [budgetId],
    queryFn: () =>
      client.get<Budget>(`budgets/${budgetId}`).then((res) => res.data),
  }),
  paginated: (params) => ({
    queryKey: [params],
    queryFn: () => client.get<Budget[]>("budgets", { params }),
  }),
  /** One budgets list per order-set; fetches run strictly one-at-a-time. */
  forOrderSet: (orderSetId: string) => ({
    queryKey: [
      { filter: { hasSpendForOrderSetId: orderSetId }, skipPagination: true },
    ],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      limitOrderSetBudgetFetch(
        () =>
          client
            .get<Budget[]>("budgets", {
              params: {
                filter: { hasSpendForOrderSetId: orderSetId },
                skipPagination: true,
              },
              axiosOptions: { signal },
            })
            .then((res) => res.data),
        signal
      ),
  }),
  overBudgetBrands: (params) => ({
    queryKey: [params],
    queryFn: () =>
      axios
        .get<OverBudgetBrand[]>("/api/over-budget-brands", {
          params,
          paramsSerializer: (p) => query(p),
          timeout: Infinity,
        })
        .then((res) => res.data),
  }),
});

export const useBudgetQuery = (budgetId?: string) => {
  return useQuery({
    ...budgetsKeyFactory.detail(budgetId as string),
    enabled: !!budgetId,
  });
};

export const useOverBudgetBrandsQuery = (
  params: Record<string, any>,
  options: any
) => {
  return useQuery<OverBudgetBrand[]>({
    ...budgetsKeyFactory.overBudgetBrands(params),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const usePaginatedBudgetsQuery = buildPaginatedQuery(
  budgetsKeyFactory.paginated,
  {
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  }
);

/** Side-load budgets for one order-set; shared by distributor OWR + Approvals/Rollup. */
export const useBudgetsForOrderSetQuery = (
  orderSetId: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    ...budgetsKeyFactory.forOrderSet(orderSetId),
    enabled: (options?.enabled ?? true) && !!orderSetId,
    staleTime: 5 * 60 * 1000,
  });
};

export const {
  useCreate: useCreateBudget,
  useUpdate,
  useDelete,
} = buildResourceMutations({
  resource: "budgets",
  detailQueryKey: (id) => budgetsKeyFactory.detail(id).queryKey,
  // Root key so paginated lists + forOrderSet side-loads stay in sync.
  listQueryKey: budgetsKeyFactory._def,
});

type OrderWindowBudgetSpendResponse = {
  budgetId: string;
  orderWindowPendingAmount: string;
};

export const useOrderWindowBudgetSpendReport = (
  ocmId: string,
  brandId: string
) => {
  const { currentChannel: channel, currentTerritory: territoryId } =
    useSelector((state: any) => state.user);
  const filter = {
    orderCalendarMonthId: ocmId,
    brandId,
    channel,
    territoryId,
  };
  return useQuery({
    queryKey: ["order-window-budget-spend-report", filter],
    queryFn: () =>
      client
        .get<any[]>("order-window-budget-spend-report", {
          params: { filter },
          deserializeOutput: false,
        })
        .then(
          (res) => res.map(camelCaseKeys) as OrderWindowBudgetSpendResponse[]
        ),
    staleTime: 5 * 60 * 1000,
  });
};
